'use client';

import { useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { VisualizationWrapper } from './visualization-wrapper';
import { useVisualizationContext } from './context';

interface FlowchartNode {
  id: string;
  label: string;
  type: 'start' | 'end' | 'process' | 'decision' | 'input' | 'output';
  description?: string;
}

interface FlowchartEdge {
  from: string;
  to: string;
  label?: string;
  condition?: 'yes' | 'no' | 'true' | 'false';
}

interface FlowchartProps {
  element: {
    props: {
      title: string;
      nodes: FlowchartNode[];
      edges: FlowchartEdge[];
    };
    raw?: boolean;
    visualizationId?: string;
  };
}

const nodeColors: Record<FlowchartNode['type'], string> = {
  start: '#22c55e',
  end: '#ef4444',
  process: '#3b82f6',
  decision: '#f59e0b',
  input: '#8b5cf6',
  output: '#06b6d4',
};

// Node dimensions
const NODE_WIDTH = 160;
const NODE_HEIGHT = 60;
const DECISION_SIZE = 100;
const HORIZONTAL_GAP = 60;
const VERTICAL_GAP = 80;

// Simple hierarchical layout algorithm (topological sort + level assignment)
function getLayoutedElements(
  nodes: Node[], 
  edges: Edge[],
  rawNodes: FlowchartNode[]
): { nodes: Node[]; edges: Edge[] } {
  if (!nodes.length) return { nodes, edges };

  // Build adjacency list and find node levels using BFS from start nodes
  const outgoing = new Map<string, string[]>();
  const incoming = new Map<string, string[]>();
  
  nodes.forEach(n => {
    outgoing.set(n.id, []);
    incoming.set(n.id, []);
  });
  
  edges.forEach(e => {
    outgoing.get(e.source)?.push(e.target);
    incoming.get(e.target)?.push(e.source);
  });

  // Find start nodes (no incoming edges or type 'start')
  const startNodes = nodes.filter(n => {
    const rawNode = rawNodes.find(r => r.id === n.id);
    return rawNode?.type === 'start' || incoming.get(n.id)?.length === 0;
  });

  // BFS to assign levels
  const levels = new Map<string, number>();
  const queue: { id: string; level: number }[] = startNodes.map(n => ({ id: n.id, level: 0 }));
  
  while (queue.length) {
    const { id, level } = queue.shift()!;
    
    // Take the maximum level if already visited
    if (levels.has(id) && levels.get(id)! >= level) continue;
    levels.set(id, level);
    
    outgoing.get(id)?.forEach(targetId => {
      queue.push({ id: targetId, level: level + 1 });
    });
  }

  // Handle any unvisited nodes (disconnected)
  nodes.forEach(n => {
    if (!levels.has(n.id)) {
      levels.set(n.id, 0);
    }
  });

  // Group nodes by level
  const nodesByLevel = new Map<number, Node[]>();
  nodes.forEach(n => {
    const level = levels.get(n.id) || 0;
    if (!nodesByLevel.has(level)) nodesByLevel.set(level, []);
    nodesByLevel.get(level)!.push(n);
  });

  // Calculate positions
  const maxLevel = Math.max(...Array.from(levels.values()));
  const layoutedNodes = nodes.map(node => {
    const level = levels.get(node.id) || 0;
    const nodesAtLevel = nodesByLevel.get(level) || [];
    const indexAtLevel = nodesAtLevel.indexOf(node);
    const countAtLevel = nodesAtLevel.length;
    
    const rawNode = rawNodes.find(r => r.id === node.id);
    const isDecision = rawNode?.type === 'decision';
    const nodeWidth = isDecision ? DECISION_SIZE : NODE_WIDTH;
    
    // Center nodes at each level
    const totalWidth = countAtLevel * nodeWidth + (countAtLevel - 1) * HORIZONTAL_GAP;
    const startX = -totalWidth / 2;
    
    return {
      ...node,
      position: {
        x: startX + indexAtLevel * (nodeWidth + HORIZONTAL_GAP),
        y: level * (NODE_HEIGHT + VERTICAL_GAP),
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}

export function Flowchart({ element }: FlowchartProps) {
  if (!element?.props?.nodes?.length) {
    return <div className="p-4 border rounded-lg bg-muted/30 text-muted-foreground">Loading flowchart...</div>;
  }

  const { title, nodes: rawNodes, edges: rawEdges } = element.props;

  // Build and layout nodes and edges
  const { layoutedNodes, layoutedEdges } = useMemo(() => {
    const rfNodes: Node[] = rawNodes.map((node) => ({
      id: node.id,
      position: { x: 0, y: 0 }, // Will be set by layout
      data: { 
        label: (
          <div className="text-center">
            <div className="font-medium text-sm">{node.label}</div>
            {node.description && (
              <div className="text-xs opacity-80 mt-1">{node.description}</div>
            )}
          </div>
        ),
        nodeType: node.type,
      },
      style: {
        background: nodeColors[node.type],
        color: '#fff',
        padding: node.type === 'decision' ? 12 : 16,
        borderRadius: node.type === 'start' || node.type === 'end' ? '50%' : 
                      node.type === 'decision' ? 4 : 8,
        width: node.type === 'decision' ? DECISION_SIZE : NODE_WIDTH,
        minHeight: node.type === 'decision' ? DECISION_SIZE : NODE_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center' as const,
        border: '2px solid rgba(255,255,255,0.2)',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
      },
    }));

    const rfEdges: Edge[] = rawEdges.map((edge, index) => ({
      id: `edge-${index}`,
      source: edge.from,
      target: edge.to,
      label: edge.label || edge.condition,
      type: 'smoothstep',
      animated: false,
      markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b' },
      style: { strokeWidth: 2, stroke: '#64748b' },
      labelStyle: { 
        fill: '#94a3b8', 
        fontSize: 11,
        fontWeight: 600,
      },
      labelBgStyle: {
        fill: '#1e293b',
        fillOpacity: 0.95,
        rx: 4,
        ry: 4,
      },
      labelBgPadding: [6, 4] as [number, number],
    }));

    const { nodes, edges } = getLayoutedElements(rfNodes, rfEdges, rawNodes);
    return { layoutedNodes: nodes, layoutedEdges: edges };
  }, [rawNodes, rawEdges]);

  const [nodes, , onNodesChange] = useNodesState(layoutedNodes);
  const [edges, , onEdgesChange] = useEdgesState(layoutedEdges);

  // Raw mode - return just the content for canvas
  if (element.raw) {
    return (
      <div className="w-full h-full">
        <FlowContent nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} forceInteractive />
      </div>
    );
  }

  return (
    <VisualizationWrapper
      title={title}
      minHeight={500}
      exportData={{
        type: 'flowchart',
        content: element.props
      }}
      visualizationId={element.visualizationId}
    >
      <div className="w-full h-full">
        <FlowContent nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} />
      </div>
    </VisualizationWrapper>
  );
}

/** Inner component that reads visualization context for fullscreen detection */
function FlowContent({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  forceInteractive = false,
}: {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: ReturnType<typeof useNodesState>[2];
  onEdgesChange: ReturnType<typeof useEdgesState>[2];
  forceInteractive?: boolean;
}) {
  const { isFullscreen } = useVisualizationContext();
  // On mobile, always enable pan/zoom even in normal view since visualizations may not fit
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const interactive = forceInteractive || isFullscreen || isMobile;

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      fitView
      fitViewOptions={{ padding: 0.3 }}
      className="bg-background"
      nodesDraggable={interactive && isFullscreen}
      nodesConnectable={false}
      elementsSelectable={interactive}
      panOnDrag={interactive}
      zoomOnScroll={interactive}
      zoomOnPinch={true}
      zoomOnDoubleClick={interactive}
      preventScrolling={isMobile}
      minZoom={0.2}
      maxZoom={2}
    >
      <Background gap={20} size={1} />
      {interactive && <Controls showInteractive={false} />}
      {interactive && (
        <MiniMap
          nodeColor={(node) => node.style?.background as string || '#888'}
          maskColor="rgba(0,0,0,0.1)"
          className="!bg-muted/50"
        />
      )}
    </ReactFlow>
  );
}
